// useArtifactConfigs - laedt externalized parameters pro Artefakt fuer ALLE Vorwaerts-Stufen
// in EINEM Multi-Endpoint-Aufruf (Etappe A: POST /api/transport/artifact-configs-multi).
// Cache-Key = getArtifactCacheKey(sourceId, targetInstanceId, werkKennung) - basiert auf der
// Basis-Stufe (erste Vorwaerts-Stufe), damit die Werk-Isolation wie bisher greift.
// Antwort wird als SET_CFG in den Context gehoben (entries + stageValues).

import { useCallback } from "react";
import { api } from "../../../utils/helpers";
import { plantNamingError } from "../../../utils/transportBatchErrors";
import {
  getArtifactCacheKey,
  normalizeConfigEntry,
} from "../../../utils/transport-config-helpers";
import { useTransportV2, vorwaertsAlleZiele } from "../context/TransportV2Context";

export function useArtifactConfigs() {
  const { state, dispatch } = useTransportV2();

  const loadFor = useCallback(async ({ sourceInstanceId, targetInstanceId, sourceArtifactId, suffixedId, werkKey, werkKennung }, { signal, commitAfter, readScope } = {}) => {
    if (signal?.aborted) return;
    if (werkKennung && !suffixedId) throw Object.assign(new Error(plantNamingError({ code: "PLANT_TARGET_MISSING" })), { code: "PLANT_TARGET_MISSING" });
    const cacheKey = JSON.stringify([getArtifactCacheKey(sourceArtifactId, targetInstanceId, werkKennung), targetInstanceId, suffixedId || sourceArtifactId,
      vorwaertsAlleZiele(state).map(st => [st.key, st.instanceId]), readScope]);
    // Wenn schon geladen: nichts tun.
    const existing = state.cfg?.[werkKey]?.[sourceArtifactId];
    if (existing && existing.cacheKey === cacheKey) return existing.entries;

    // Alle Vorwaerts-Stufen des Space als targets. Basis = erste Stufe (typischerweise QAS
    // oder die per Space konfigurierte Ziel-Stufe). Ohne Vorwaerts-Stufen kann nichts geladen
    // werden - der alte Legacy-Fall (nur targetInstanceId als Ziel) wird als Fallback bedient.
    const stufen = vorwaertsAlleZiele(state);
    const targets = [];
    if (stufen.length > 0) {
      for (const st of stufen) {
        // The comparison supplies the canonical target ID for every forward stage.
        const targetFlowId = suffixedId || sourceArtifactId;
        // Im Mehrzielmodus werden ALLE Ziele gelesen (Vertrag V.10/V.11: die Kette hängt nicht an der Auswahl); jede Spalte ihren targetKey (Vertrag V.7); die Antwort liegt unter byStage[key].
        targets.push(state.multiTarget ? { stage: st.stageKey, key: st.key, targetInstanceId: st.instanceId, targetFlowId }
          : { stage: st.key, targetInstanceId: st.instanceId, targetFlowId });
      }
    } else {
      // Legacy-Fallback: keine Vorwaerts-Stufen ableitbar (z.B. Test ohne Scene0-State).
      targets.push({ stage: "qas", targetInstanceId, targetFlowId: suffixedId || sourceArtifactId });
    }

    try {
      const r = await api("/api/transport/artifact-configs-multi", {
        method: "POST",
        timeout: 60000,
        retry: false,
        signal,
        body: JSON.stringify({
          sourceInstanceId,
          sourceArtifactId,
          targets,
        }),
      });
      if (!Array.isArray(r?.base)) throw new Error("Invalid parameter response");
      const base = r.base;
      const byStage = (r && r.byStage && typeof r.byStage === "object") ? r.byStage : {};
      // A new target artifact has no configuration yet. Only that explicit
      // not-found response is empty data; authorization/network errors stay errors.
      const failedStages = Object.entries(byStage).filter(([, value]) => value?.error && value.error !== "HTTP 404");
      if (failedStages.length) throw new Error(failedStages.map(([stage, value]) => `${stage}: ${value.error}`).join("; "));
      // Basis-Stufe: deren Ist-Werte fliessen in normalizeConfigEntry(targetValue) und werden
      // damit zur Vorbelegung des Root-Eintrags (newValue). Spaetere Stufen wandern nach byStage.
      const basisStageKey = targets[0]?.key || targets[0]?.stage || null;
      const basisValuesArr = basisStageKey ? (byStage[basisStageKey]?.values || []) : [];
      const basisValueLookup = {};
      for (const v of basisValuesArr) {
        if (v && v.ParameterKey != null) basisValueLookup[v.ParameterKey] = v.targetValue;
      }
      const entries = base
        .map((cfg) => {
          const targetValue = basisValueLookup[cfg.ParameterKey];
          // normalizeConfigEntry erwartet targetValue als Property des raw-Eintrags.
          return normalizeConfigEntry({ ...cfg, targetValue: targetValue == null ? undefined : targetValue });
        })
        .filter(Boolean);
      // stageValues: alle spaeteren Stufen (nicht Basis) - byStage-Enrichment im Reducer.
      const stageValues = {};
      for (const [stageKey, payload] of Object.entries(byStage)) {
        if (stageKey === basisStageKey) continue;
        stageValues[stageKey] = payload?.values || [];
      }
      // Read SAP and Git concurrently, but merge only after a valid Git response.
      // SET_GIT_CFG is dispatched before this barrier resolves, so the reducer
      // applies its existing Git and typed-value precedence in either arrival order.
      if (commitAfter && !(await commitAfter)) return;
      if (signal?.aborted) return;
      dispatch({ type: "SET_CFG", payload: { werkKey, sourceId: sourceArtifactId, cacheKey, entries, stageValues } });
      return entries;
    } catch (e) {
      if (!signal?.aborted) dispatch({ type: "SET_CFG_ERROR", payload: { key: `${werkKey}:${sourceArtifactId}`, error: e?.message || "Parameter read failed" } });
      throw e;
    }
    // Bewusst schmale Deps: state.cfg (Cache-Check) + state.space (Stufenableitung).
    // Ein umfassenderes [state] wuerde loadFor bei jedem Reducer-Change neu erstellen und
    // in Scene4 unnoetiges Nachladen anstossen.
  }, [state.cfg, state.space, state.multiTarget, state.stageTargets, dispatch]);

  return { loadFor };
}
